import Big from 'big.js';
import { Formik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  Button,
  Buttons,
  CancelButton,
  Currency,
  CurrencyInput,
  Field,
  Form,
  Icon,
  ModalCard,
  Table,
  TableTitle,
  Tooltip,
} from '~/components';
import { Card } from '~/components/ModalCard';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDateTimeFormat } from '~/hooks';
import useAuth from '~/hooks/useAuth.js';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { emptyStringToNull } from '~/utils';
import RevalueConfirmation from '../../rate-cards/RevalueConfirmation';
import { describeWindow } from '../../rate-cards/timeline';

const Modal = styled(ModalCard)`
  ${Card} {
    width: ${({ $wide }) => ($wide ? '66rem' : '42rem')};
  }
`;

const COST_FIELDS = ['overrideCostRate', 'costCurrency', 'costPerHour', 'overheadCostPerHour'];

export default function ProjectMemberCostRateModal({ project, projectMember, onSaved, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const auth = useAuth();
  const dateTimeFormat = useDateTimeFormat();

  const projectMemberId = projectMember.id;

  const [member, setMember] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(project.id)
          .members(projectMemberId)
          .get();
        setMember(data);
      } catch {
        // Keep the modal in its loading state.
      }
    })();
  }, [api, workspace.id, project.id, projectMemberId]);

  const canEditCost = auth.workspace.manage;
  const hasMultipleRateCards = (member?.rateCards?.length ?? 0) > 1;

  const initialValues = {
    costCurrency: member?.rateCards?.[0]?.costCurrency || project.currency,
    rateCards: (member?.rateCards ?? []).map((rateCard) => ({
      ...rateCard,
      overrideCostRate: rateCard.overrideCostRate ?? false,
      costPerHour: rateCard.costPerHour ?? '',
      overheadCostPerHour: rateCard.overheadCostPerHour ?? '',
    })),
  };

  const handleSubmit = async (values, formik) => {
    const saved = _.keyBy(member.rateCards, 'projectRateCardId');
    const rateCards = values.rateCards
      .map((row) =>
        emptyStringToNull({
          projectRateCardId: row.projectRateCardId,
          overrideCostRate: Boolean(row.overrideCostRate),
          costCurrency: values.costCurrency,
          costPerHour: row.costPerHour,
          overheadCostPerHour: row.overheadCostPerHour,
        }),
      )
      .filter((row) => COST_FIELDS.some((field) => row[field] !== (saved[row.projectRateCardId][field] ?? null)));

    const tracked = rateCards.map((row) => saved[row.projectRateCardId]).filter(({ hasTimeEntries }) => hasTimeEntries);
    if (tracked.length > 0) {
      const confirmed = await confirmation.prompt((resolve) => (
        <RevalueConfirmation rateCards={hasMultipleRateCards ? tracked : null} resolve={resolve} />
      ));
      if (!confirmed) return;
    }

    formik.setSubmitting(true);

    try {
      await api.www.workspaces(workspace.id).projectSettings(project.id).members(projectMemberId).upsert({ rateCards });
      toast.success('Project Member saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
      formik.setSubmitting(false);
    }
  };

  return (
    <Modal
      title={canEditCost ? 'Edit Cost Rate' : 'View Cost Rate'}
      byline={projectMember.member?.name}
      onClose={onClose}
      $wide={hasMultipleRateCards}
      data-testid="edit_cost_rate_modal">
      {!member ? (
        <ModalCard.Body>
          <PageLoader />
        </ModalCard.Body>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            costCurrency: Yup.string().label('Cost Currency').nullable(),
            rateCards: Yup.array().of(
              Yup.object().shape({
                overrideCostRate: Yup.bool(),
                costPerHour: Yup.number().label('Labor Cost Per Hour').min(0).max(99999999999).nullable(),
                overheadCostPerHour: Yup.number().label('Overhead Cost Per Hour').min(0).max(99999999999).nullable(),
              }),
            ),
          })}>
          {({ values, ...formik }) => {
            const [firstCard] = values.rateCards;
            const isReadOnly = !canEditCost || !firstCard?.overrideCostRate;

            const totalCostPerHour =
              _.isNumber(firstCard?.costPerHour) || _.isNumber(firstCard?.overheadCostPerHour)
                ? Big(firstCard.costPerHour || 0)
                    .plus(firstCard.overheadCostPerHour || 0)
                    .toNumber()
                : null;

            return (
              <Form>
                <ModalCard.Body>
                  {!hasMultipleRateCards ? (
                    <>
                      <Form.Control>
                        <Field.Checkbox
                          label={
                            <div style={{ display: 'flex' }}>
                              Override Cost Rate
                              {['fixed_hourly', 'fixed_monthly'].includes(member.member.costMethodId) && (
                                <Tooltip message="This member has a fixed labor cost, so their cost is based on their availability rather than the hours they work. An overridden cost rate will not change their cost.">
                                  <Icon icon="exclamation-triangle" color={colors.warning} spaceLeft />
                                </Tooltip>
                              )}
                            </div>
                          }
                          name="rateCards.0.overrideCostRate"
                          disabled={!canEditCost}
                        />
                      </Form.Control>

                      <Form.Control>
                        <Field.WorkspaceCurrencySelect
                          name="costCurrency"
                          placeholder="Cost Currency"
                          clearable={false}
                          disabled={isReadOnly}
                        />
                      </Form.Control>

                      <Form.Control>
                        <Field.Currency
                          name="rateCards.0.costPerHour"
                          placeholder="Labor Cost Per Hour"
                          precision={7}
                          currency={values.costCurrency}
                          disabled={isReadOnly}
                        />
                      </Form.Control>

                      <Form.Control>
                        <Field.Currency
                          name="rateCards.0.overheadCostPerHour"
                          placeholder="Overhead Cost Per Hour"
                          precision={7}
                          currency={values.costCurrency}
                          disabled={isReadOnly}
                        />
                      </Form.Control>

                      <Form.Control>
                        <CurrencyInput
                          placeholder="Total Cost Per Hour"
                          value={totalCostPerHour}
                          currency={values.costCurrency}
                          precision={7}
                          disabled
                          onChange={() => {}}
                        />
                      </Form.Control>
                    </>
                  ) : (
                    <>
                      <Form.Control>
                        <Field.WorkspaceCurrencySelect
                          name="costCurrency"
                          placeholder="Cost Currency"
                          clearable={false}
                          disabled={!canEditCost}
                        />
                      </Form.Control>

                      <TableTitle>Rate Cards</TableTitle>

                      <Table small>
                        <Table.Header>
                          <Table.Column minWidth="16rem">Date Range</Table.Column>
                          <Table.Column width="11rem" align="center">
                            Override Cost
                            {['fixed_hourly', 'fixed_monthly'].includes(member.member.costMethodId) && (
                              <Tooltip message="This member has a fixed labor cost, so their cost is based on their availability rather than the hours they work. An overridden cost rate will not change their cost.">
                                <Icon icon="exclamation-triangle" color={colors.warning} spaceLeft />
                              </Tooltip>
                            )}
                          </Table.Column>
                          <Table.Column width="12rem" align="right">
                            Cost/Hour
                          </Table.Column>
                          <Table.Column width="12rem" align="right">
                            Overhead/Hour
                          </Table.Column>
                          <Table.Column width="10rem" align="right">
                            Total/Hour
                          </Table.Column>
                        </Table.Header>

                        <Table.Body>
                          {values.rateCards.map((row, index) => (
                            <Table.Row key={row.projectRateCardId} data-testid="rate_card_rate_row">
                              <Table.Cell>
                                #{row.projectRateCard.number} · {describeWindow(row, dateTimeFormat)}
                                {row.hasTimeEntries && (
                                  <Tooltip Container="span" message="Time has been tracked in this period">
                                    <Icon icon="clock" color={colors.grey40} spaceLeft data-testid="tracked_time" />
                                  </Tooltip>
                                )}
                              </Table.Cell>
                              <Table.Cell>
                                <Field.Checkbox name={`rateCards.${index}.overrideCostRate`} disabled={!canEditCost} />
                              </Table.Cell>
                              <Table.Cell>
                                <Field.Currency
                                  name={`rateCards.${index}.costPerHour`}
                                  precision={7}
                                  currency={values.costCurrency}
                                  disabled={!canEditCost || !row.overrideCostRate}
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Field.Currency
                                  name={`rateCards.${index}.overheadCostPerHour`}
                                  precision={7}
                                  currency={values.costCurrency}
                                  disabled={!canEditCost || !row.overrideCostRate}
                                />
                              </Table.Cell>
                              <Table.Cell data-testid="total_cost_per_hour">
                                <Currency
                                  value={
                                    row.overrideCostRate &&
                                    (_.isNumber(row.costPerHour) || _.isNumber(row.overheadCostPerHour))
                                      ? Big(row.costPerHour || 0)
                                          .plus(row.overheadCostPerHour || 0)
                                          .toNumber()
                                      : null
                                  }
                                  currency={values.costCurrency}
                                  maximumFractionDigits={7}
                                />
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </>
                  )}
                </ModalCard.Body>

                <ModalCard.Footer>
                  <Buttons align="right">
                    <CancelButton onClick={onClose}>Close</CancelButton>

                    {canEditCost && (
                      <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                        Save &amp; Close
                      </Button>
                    )}
                  </Buttons>
                </ModalCard.Footer>
              </Form>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
}
